import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import AlertTitle from "@mui/material/AlertTitle"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import { formatDistanceToNow } from "date-fns"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { selectCRMSettings, updateCRMSettings } from "../reducer"
import { getSmsSendingPrerequisiteState } from "../utils"
import { A2PFailureList, SupportLink } from "./A2PFailureList"
import A2PFormErrors, { a2pErrorMessages } from "./A2PFormErrors"
import ResubmitBrandDialog from "./ResubmitBrandDialog"
import { A2P_REASONS } from "./a2pStatus"

const TCR_OTP_SENDER = "+1 (915) 278-2000"

function ago(value) {
  if (!value) {
    return null
  }
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true })
  } catch {
    return null
  }
}

/**
 * The one place a client sees where their business texting registration
 * stands and what to do next. Driven by `a2p_status` from the backend.
 */
export default function A2PStatusPanel({ onContinueRegistration }) {
  const crmSettings = useSelector(selectCRMSettings)
  const settings = crmSettings?.settings || {}
  const status = settings.a2p_status || {}
  const gate = getSmsSendingPrerequisiteState(crmSettings)
  const axios = useAuthorizedAxiosClient()
  const dispatch = useDispatch()
  const [refreshing, setRefreshing] = useState(false)
  const [resending, setResending] = useState(false)
  const [message, setMessage] = useState(null)
  const [errors, setErrors] = useState([])
  const [resubmitOpen, setResubmitOpen] = useState(false)

  function refresh() {
    setRefreshing(true)
    setErrors([])
    setMessage(null)
    axios
      .post("/crm/api/twilio/a2p-10dlc/status/")
      .then(({ data }) => {
        dispatch(updateCRMSettings(data))
        setMessage("Status updated just now.")
      })
      .catch((error) => setErrors(a2pErrorMessages(error)))
      .finally(() => setRefreshing(false))
  }

  function resendOtp() {
    setResending(true)
    setErrors([])
    setMessage(null)
    axios
      .post("/crm/api/twilio/a2p-10dlc/sole-prop/resend-otp/")
      .then(({ data }) => {
        dispatch(updateCRMSettings(data))
        setMessage("We sent the confirmation text again.")
      })
      .catch((error) => setErrors(a2pErrorMessages(error)))
      .finally(() => setResending(false))
  }

  const refreshButton = (
    <LoadingButton variant="outlined" loading={refreshing} onClick={refresh}>
      Refresh status
    </LoadingButton>
  )
  const footer = (
    <>
      {message && (
        <Typography variant="body2" color="success.main">
          {message}
        </Typography>
      )}
      <A2PFormErrors messages={errors} />
    </>
  )

  let body = null
  switch (gate.reason) {
    case A2P_REASONS.brandPending:
      body = (
        <Alert severity="info" sx={{ textAlign: "left" }}>
          <AlertTitle>Your business registration is in review</AlertTitle>
          <Column sx={{ gap: 1, alignItems: "flex-start" }}>
            <Typography variant="body2">
              {status.brand_started_at
                ? `You submitted it ${ago(status.brand_started_at)}. `
                : ""}
              Carriers usually respond within a few days (up to 7 business
              days). We'll email you as soon as there's an update.
            </Typography>
            <Row sx={{ gap: 1 }}>{refreshButton}</Row>
          </Column>
        </Alert>
      )
      break
    case A2P_REASONS.brandFailed:
      body = (
        <Alert severity="error" sx={{ textAlign: "left" }}>
          <AlertTitle>Your business registration was rejected</AlertTitle>
          <Column sx={{ gap: 2, alignItems: "flex-start" }}>
            <A2PFailureList failures={status.brand_failure} />
            <Typography variant="body2">
              Fix the details below and resubmit. Your first 3 resubmissions are
              free. If you don't have your own business EIN, please{" "}
              <SupportLink /> to switch to a sole proprietor registration.
            </Typography>
            <Row sx={{ gap: 1, flexWrap: "wrap" }}>
              <Button variant="contained" onClick={() => setResubmitOpen(true)}>
                Edit &amp; resubmit
              </Button>
              {refreshButton}
            </Row>
          </Column>
        </Alert>
      )
      break
    case A2P_REASONS.brandSuspended:
    case A2P_REASONS.campaignSuspended:
      body = (
        <Alert severity="error" sx={{ textAlign: "left" }}>
          <AlertTitle>Texting registration suspended</AlertTitle>
          <Typography variant="body2">
            The carriers suspended this registration. Please <SupportLink /> so
            we can help you resolve it.
          </Typography>
        </Alert>
      )
      break
    case A2P_REASONS.otpPending:
      body = (
        <Alert severity="warning" sx={{ textAlign: "left" }}>
          <AlertTitle>Action needed: confirm by text</AlertTitle>
          <Column sx={{ gap: 1, alignItems: "flex-start" }}>
            <Typography variant="body2">
              We texted{" "}
              <strong>{status.sole_prop_mobile_phone || "your mobile"}</strong>{" "}
              from {TCR_OTP_SENDER}. Reply <strong>YES</strong> to that text
              within 24 hours to confirm your registration.
              {status.otp_sent_at ? ` (Sent ${ago(status.otp_sent_at)}.)` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No text, or it expired? Send it again. Wrong number? Please{" "}
              <SupportLink />.
            </Typography>
            <Row sx={{ gap: 1, flexWrap: "wrap" }}>
              <LoadingButton
                variant="contained"
                loading={refreshing}
                onClick={refresh}
              >
                I replied YES — check now
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                loading={resending}
                onClick={resendOtp}
              >
                Resend text
              </LoadingButton>
            </Row>
          </Column>
        </Alert>
      )
      break
    case A2P_REASONS.campaignPending:
      body = (
        <Alert severity="info" sx={{ textAlign: "left" }}>
          <AlertTitle>
            Almost there: your texting campaign is in review
          </AlertTitle>
          <Column sx={{ gap: 1, alignItems: "flex-start" }}>
            <Typography variant="body2">
              Your business is approved. Carriers are reviewing your texting
              campaign
              {status.sms_phone ? ` for ${status.sms_phone}` : ""}; this usually
              takes a few days. We'll email you when you can start texting.
            </Typography>
            <Row sx={{ gap: 1 }}>{refreshButton}</Row>
          </Column>
        </Alert>
      )
      break
    case A2P_REASONS.campaignFailed:
      body = (
        <Alert severity="error" sx={{ textAlign: "left" }}>
          <AlertTitle>Your texting campaign was rejected</AlertTitle>
          <Column sx={{ gap: 2, alignItems: "flex-start" }}>
            <A2PFailureList failures={status.campaign_failure} />
            <Typography variant="body2">
              Please <SupportLink /> and we'll help you fix and resubmit it.
            </Typography>
          </Column>
        </Alert>
      )
      break
    case A2P_REASONS.registrationIncomplete:
      body =
        status.phase === "campaign_needed" ? (
          <Alert severity="success" sx={{ textAlign: "left" }}>
            <AlertTitle>Your business is approved</AlertTitle>
            <Column sx={{ gap: 1, alignItems: "flex-start" }}>
              <Typography variant="body2">
                Next step: describe the texts you'll send so carriers can
                approve your texting campaign. We'll then set up your number.
              </Typography>
              <Button variant="contained" onClick={onContinueRegistration}>
                Finish setup
              </Button>
            </Column>
          </Alert>
        ) : (
          <Column sx={{ gap: 2, alignItems: "flex-start" }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "left" }}
            >
              Register your business for texting (A2P 10DLC) to send SMS
              campaigns to candidates.
            </Typography>
            <Button variant="contained" onClick={onContinueRegistration}>
              {status.phase === "in_progress"
                ? "Continue SMS registration"
                : "Start SMS registration"}
            </Button>
          </Column>
        )
      break
    default:
      if (gate.ready) {
        body = (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "left" }}
          >
            SMS is configured
            {status.sms_phone ? ` (texting from ${status.sms_phone})` : ""}. Use
            Send SMS on a candidate to pick a template.
          </Typography>
        )
      }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Column sx={{ gap: 1, alignItems: "stretch" }}>
        {body}
        {footer}
      </Column>
      <ResubmitBrandDialog
        open={resubmitOpen}
        onClose={() => setResubmitOpen(false)}
        failures={status.brand_failure}
        soleProprietor={status.brand_type === "SOLE_PROPRIETOR"}
      />
    </Box>
  )
}
